import { useEffect, useState } from 'react';
import { Select, Tooltip } from 'antd';
import priceListApi from '../../masters/priceLists/priceListApi';

// ════════════════════════════════════════════════════════════════════
// Stage 6PL-1 (migration 0185) — which price list THIS document uses.
//
// Normally the customer decides, and that stays the default. This is the
// escape hatch for the two things the customer-level mapping cannot express:
//
//   • quote this one document off a DIFFERENT card
//   • quote this one document at MASTER rates, ignoring cards entirely
//
// Both were already possible by retyping every line (a typed rate stamps
// MANUAL and is immune), which works and is tedious enough that people did it
// inconsistently. This makes the intent explicit and recorded on the document.
//
// It deliberately does NOT change the customer's own mapping — one document,
// one decision. And changing it never re-prices silently: the editor runs the
// same diff prompt a customer swap does, so hand-typed lines stay untouched.
// ════════════════════════════════════════════════════════════════════

// Automatic is the overwhelming case, so it stays quiet; an override is the
// exception and is coloured, because it changes what the customer is charged
// and must not look like a default anyone can scroll past.
const PICKER_CSS = `
/* Built to the same geometry as the Wastage Mode select one zone above, so the
   plate reads as one instrument: 32px tall, 8px radius, a real border and the
   standard caret. It was a borderless chip with a tag icon, which read as a
   status badge rather than something you could open. */
.pf-pl .ant-select-selector{font-size:12.5px!important;height:32px!important;border-radius:8px!important;
  padding:0 12px!important;display:flex;align-items:center;background:#fff!important;border-color:#e2e8f0!important;}
.pf-pl.ant-select:hover .ant-select-selector,.pf-pl.ant-select-focused .ant-select-selector{border-color:#1877F2!important;}
.pf-pl .ant-select-selection-item{font-size:12.5px;line-height:30px;letter-spacing:.01em;}
.pf-pl .ant-select-arrow{color:#94a3b8;}
/* AUTO is the overwhelming case, so it stays quiet. */
.pf-pl-auto .ant-select-selection-item{color:#64748b;font-weight:500;}
/* An override is the exception and is coloured, because it changes what the
   customer is charged and must not look like a default anyone can scroll past. */
.pf-pl-override .ant-select-selector{background:#fff7ed!important;border-color:#fed7aa!important;}
.pf-pl-override .ant-select-selection-item{color:#b45309;font-weight:700;}
.pf-pl-override .ant-select-arrow{color:#b45309;}
.pf-pl-none .ant-select-selector{background:#f1f5f9!important;}
.pf-pl-none .ant-select-selection-item{color:#475569;font-weight:600;}
.pf-pl-pop .ant-select-item{font-size:11px;min-height:26px;line-height:20px;padding:4px 10px;}
.pf-pl-pop .ant-select-item-option-selected{background:#e8f0fe!important;color:#0f5fd6;font-weight:700;}
`;
const AUTO = 'AUTO';
const NONE = 'NONE';

export default function PriceListPicker({ header, setHeader, readOnly }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Soft-fail: a price-list outage must never block quoting. The picker just
    // shows Automatic and the engine keeps doing what it always did.
    priceListApi.assignable()
      .then((r) => { if (alive) setLists(Array.isArray(r) ? r : []); })
      .catch(() => { if (alive) setLists([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const mode = header.priceListMode || AUTO;
  const value = mode === 'FIXED' ? (header.priceListOverrideId || AUTO) : mode;

  // A card chosen on an older document that has since been archived would
  // otherwise vanish from the control and read as "Automatic", quietly
  // misdescribing what the document actually says.
  const known = lists.some((l) => l.id === header.priceListOverrideId);
  // No "Price list:" prefix — the zone this sits in is already labelled PRICE
  // LIST, and repeating it made every option read "Price list: Price list: …".
  const options = [
    { value: AUTO, label: 'Automatic' },
    ...(mode === 'FIXED' && header.priceListOverrideId && !known
      ? [{ value: header.priceListOverrideId, label: '(archived card)' }]
      : []),
    ...lists.map((l) => ({
      value: l.id,
      label: `${l.listCode}${l.status === 'DRAFT' ? ' (draft)' : ''}`
    })),
    { value: NONE, label: 'None — master rates' }
  ];

  const onChange = (v) => {
    if (v === AUTO) setHeader((h) => ({ ...h, priceListMode: AUTO, priceListOverrideId: null }));
    else if (v === NONE) setHeader((h) => ({ ...h, priceListMode: NONE, priceListOverrideId: null }));
    else setHeader((h) => ({ ...h, priceListMode: 'FIXED', priceListOverrideId: v }));
  };

  const tip = mode === NONE
    ? 'This document ignores price lists and quotes the item master. The customer’s own mapping is unchanged.'
    : (mode === 'FIXED'
      ? 'This document is priced off a card chosen here, not the customer’s usual one. The customer’s own mapping is unchanged.'
      : 'Priced by whichever card reaches this customer — the normal behaviour.');

  const tone = mode === AUTO ? 'pf-pl-auto' : (mode === NONE ? 'pf-pl-none' : 'pf-pl-override');

  return (
    <Tooltip title={tip}>
      <span>
      <style>{PICKER_CSS}</style>
      <Select
        size="small"
        value={value}
        loading={loading}
        disabled={readOnly}
        onChange={onChange}
        options={options}
        popupClassName="pf-pl-pop"
        popupMatchSelectWidth={260}
        style={{ minWidth: 178 }}
        className={`pf-pl ${tone}`}
      />
      </span>
    </Tooltip>
  );
}
